import { api, apiClient } from "../lib";

export const REPORT_IMAGE_NOT_AVAILABLE_MESSAGE =
  "A selected report photo couldn't be loaded. Check the case's report photos and try again.";

type ReportImageUrl = { url: string };

const getDownloadUrl = async (
  key: string,
  fileName?: string
): Promise<ReportImageUrl> => {
  if (
    /^\/(?:api\/)?lis\/cases\/[0-9a-f-]{36}\/legacy-documents\/[0-9a-f-]{36}\/content$/i.test(
      key
    )
  ) {
    const path = key.replace(/^\/(?:api\/)?lis\//, "/");
    const response = await apiClient.get<Blob>(path, { responseType: "blob" });
    return { url: URL.createObjectURL(response.data) };
  }
  return api.post<ReportImageUrl>("/documents/download-url", { key, fileName });
};

export interface CaseImage {
  id: string;
  caseId: string;
  slideId?: string;
  imageUrl: string;
  thumbnailUrl?: string;
  fileName?: string;
  originalFileName?: string;
  fileSize?: number;
  mimeType?: string;
  caption?: string;
  description?: string;
  magnification?: string;
  displayOrder: number;
  isPrimary: boolean;
  includeInReport: boolean;
  finalizedInReport?: boolean;
  uploadedById?: string;
  uploadedBy?: {
    id: string;
    fullName?: string;
    firstName?: string;
    lastName?: string;
  };
  createdAt: string;
  updatedAt: string;
}

export interface CreateCaseImageDto {
  imageUrl: string;
  thumbnailUrl?: string;
  fileName?: string;
  fileSize?: number;
  mimeType?: string;
  caption?: string;
  magnification?: string;
  includeInReport?: boolean;
}

export interface UpdateCaseImageDto {
  caption?: string;
  description?: string;
  magnification?: string;
  displayOrder?: number;
  isPrimary?: boolean;
  includeInReport?: boolean;
  imageUrl?: string;
  fileSize?: number;
  mimeType?: string;
}

export interface PresignedUrlResponse {
  url: string;
  key: string;
}

export const caseImagesService = {
  /**
   * Get all images for a case
   */
  getImages: async (caseId: string): Promise<CaseImage[]> => {
    return api.get<CaseImage[]>(`/cases/${caseId}/images`);
  },

  /**
   * Create a new image for a case
   */
  createImage: async (
    caseId: string,
    data: CreateCaseImageDto
  ): Promise<CaseImage> => {
    return api.post<CaseImage>(`/cases/${caseId}/images`, data);
  },

  /**
   * Update an image
   */
  updateImage: async (
    caseId: string,
    imageId: string,
    data: UpdateCaseImageDto
  ): Promise<CaseImage> => {
    return api.put<CaseImage>(`/cases/${caseId}/images/${imageId}`, data);
  },

  /**
   * Delete an image
   */
  deleteImage: async (caseId: string, imageId: string): Promise<void> => {
    return api.delete(`/cases/${caseId}/images/${imageId}`);
  },

  /**
   * Toggle include in report
   */
  toggleIncludeInReport: async (
    caseId: string,
    imageId: string
  ): Promise<CaseImage> => {
    return api.patch<CaseImage>(
      `/cases/${caseId}/images/${imageId}/toggle-include`
    );
  },

  /**
   * Get presigned URL for image upload
   * Images are stored in the case-images/ folder in S3
   */
  getPresignedUrl: async (
    fileName: string,
    contentType: string
  ): Promise<PresignedUrlResponse> => {
    return api.post<PresignedUrlResponse>(
      "/documents/presigned-url/case-images",
      {
        fileName,
        contentType,
      }
    );
  },

  /**
   * Upload image file to S3 using presigned URL
   */
  uploadToS3: async (url: string, file: File): Promise<void> => {
    await fetch(url, {
      method: "PUT",
      body: file,
      headers: {
        "Content-Type": file.type,
      },
    });
  },

  /**
   * Upload a Blob to S3 using presigned URL (for camera captures)
   */
  uploadBlobToS3: async (
    url: string,
    blob: Blob,
    contentType: string
  ): Promise<void> => {
    await fetch(url, {
      method: "PUT",
      body: blob,
      headers: {
        "Content-Type": contentType,
      },
    });
  },

  /**
   * Get a presigned download URL for viewing/downloading an S3 object
   */
  getDownloadUrl: async (
    key: string,
    fileName?: string
  ): Promise<{ url: string }> => {
    return getDownloadUrl(key, fileName);
  },
};
